import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { AuthenticatedUser, loginEntra } from "../api";
import { ensureMsalInitialized } from "../auth/msal.config";

interface EntraCallbackPageProps {
  onLoginSuccess: (user: AuthenticatedUser, token: string) => void;
}

const ENTRA_SCOPES = ["openid", "profile", "email"];

function isNoTokenRequestCacheError(err: unknown): boolean {
  const e = err as { errorCode?: string; message?: string };
  return (
    e?.errorCode === "no_token_request_cache_error" ||
    (typeof e?.message === "string" && e.message.includes("no_token_request_cache_error"))
  );
}

async function waitForAccount(instance: ReturnType<typeof useMsal>["instance"]) {
  let account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  if (account) return account;

  // In redirect flow, cache hydration can lag briefly behind callback render.
  for (let i = 0; i < 8; i += 1) {
    await new Promise(resolve => setTimeout(resolve, 150));
    account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (account) return account;
  }

  return null;
}

type NavigateFn = ReturnType<typeof useNavigate>;

function redirectToLoginWithError(
  navigate: NavigateFn,
  setError: React.Dispatch<React.SetStateAction<string>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  cancelled: boolean,
  message: string,
): void {
  if (cancelled) return;
  setError(message);
  setLoading(false);
  setTimeout(() => {
    void navigate("/login?error=" + encodeURIComponent(message));
  }, 3000);
}

async function resolveIdToken(instance: ReturnType<typeof useMsal>["instance"]): Promise<string> {
  let redirectResult: Awaited<ReturnType<typeof instance.handleRedirectPromise>> | null = null;
  try {
    redirectResult = await instance.handleRedirectPromise();
  } catch (redirectErr) {
    // This can happen when /callback is opened without a matching redirect cache entry.
    // In that case, continue with account-based silent token acquisition fallback.
    if (!isNoTokenRequestCacheError(redirectErr)) {
      throw redirectErr;
    }
  }

  let idToken = redirectResult?.idToken || "";
  if (idToken) return idToken;

  const account = redirectResult?.account || (await waitForAccount(instance));
  if (!account) {
    throw new Error("No account found. Please try signing in again.");
  }

  const silentToken = await instance.acquireTokenSilent({
    scopes: ENTRA_SCOPES,
    account,
  });

  idToken = silentToken.idToken;
  if (!idToken) {
    throw new Error("No ID token returned from Entra.");
  }

  return idToken;
}

function getApiErrorMessage(apiError: any): string {
  if (apiError.response?.status === 401 || apiError.response?.status === 403) {
    return "Account not linked to Entra. Please use email/password to login first.";
  }
  if (apiError.response?.data?.message) {
    return apiError.response.data.message;
  }
  if (apiError.message) {
    return apiError.message;
  }
  return "Failed to complete login. Please try again.";
}

const EntraCallback: React.FC<EntraCallbackPageProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const handleAuthCallback = async () => {
      try {
        await ensureMsalInitialized(instance);

        const inPopupWindow = !!window.opener && window.opener !== window;
        if (inPopupWindow) {
          // Popup flow is completed by the opener via loginPopup().
          // Process any redirect hash and close this window so opener can resume.
          try {
            await instance.handleRedirectPromise();
          } catch {
            // Ignore popup callback processing errors here and return control to opener.
          }
          window.close();
          return;
        }

        const idToken = await resolveIdToken(instance);

        // Call backend endpoint to authenticate and link account
        try {
          const result = await loginEntra(idToken);

          const token = (result.token || "").trim();
          if (result.success && result.user && token) {
            // Mirrors LoginView.tsx's local-password path: a first-login user
            // must not get cached into authenticatedUser/localStorage yet —
            // doing so trapped SSO users in a reset loop (App.tsx's
            // is_first_login redirect effect would keep bouncing them back to
            // /change-password forever, since there was nothing here to ever
            // clear that stale flag from the cached object).
            if (result.user.is_first_login) {
              if (!cancelled) {
                setLoading(false);
                void navigate("/change-password", { state: { email: result.user.email } });
              }
            } else {
              onLoginSuccess(result.user, token);
              if (!cancelled) {
                setLoading(false);
                void navigate("/");
              }
            }
          } else {
            // Backend returned an error (e.g., account not linked)
            const msg = result.message || "Authentication failed. Please try again.";
            redirectToLoginWithError(navigate, setError, setLoading, cancelled, msg);
          }
        } catch (apiError: any) {
          console.error("API error during Entra login:", apiError);
          const msg = getApiErrorMessage(apiError);
          redirectToLoginWithError(navigate, setError, setLoading, cancelled, msg);
        }
      } catch (err: any) {
        console.error("Entra callback error:", err);
        const msg = err.message || "An error occurred during sign in";
        redirectToLoginWithError(navigate, setError, setLoading, cancelled, msg);
      }
    };

    void handleAuthCallback();

    return () => {
      cancelled = true;
    };
  }, [instance, navigate, onLoginSuccess]);

  let content: React.ReactNode = null;
  if (loading) {
    content = (
      <>
        <div style={{
          fontSize: "16px",
          color: "#333",
          marginBottom: "16px",
        }}>
          Signing you in...
        </div>
        <div style={{
          display: "inline-block",
          width: "32px",
          height: "32px",
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #0078d4",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  } else if (error) {
    content = (
      <>
        <div style={{
          fontSize: "18px",
          fontWeight: "bold",
          color: "#d32f2f",
          marginBottom: "16px",
        }}>
          {error}
        </div>
        <div style={{
          fontSize: "14px",
          color: "#666",
        }}>
          Redirecting to login...
        </div>
      </>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <div style={{
        backgroundColor: "white",
        padding: "40px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        textAlign: "center",
        maxWidth: "400px",
      }}>
        {content}
      </div>
    </div>
  );
};

export default EntraCallback;
